import { api } from '@goaltree/backend/convex/_generated/api'
import { USAGE_LIMITS } from '@goaltree/backend/convex/lib/usage'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { ExternalLinkIcon, InfinityIcon, SparklesIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { usePolar } from '@/hooks/use-polar'

import { SettingsGroup, SettingsRow, SettingsSection, rowId } from './primitives'

const METERS = ['planGeneration', 'brainDump', 'coachChat', 'voiceCall', 'coachMe', 'tts'] as const

export function useUsageMeters() {
  const { isAuthenticated } = useConvexAuth()
  const usage = useQuery(
    api.usage.getMyUsage,
    isAuthenticated ? { tzOffsetMin: new Date().getTimezoneOffset() } : 'skip',
  )
  const features = usage?.features
  if (!features) return { meters: null, warn: false }
  const meters = METERS.map((f) => {
    const { used, limit, period } = features[f]
    const scale = f === 'voiceCall' ? 60 : 1
    return {
      feature: f,
      period,
      used: Math.ceil(used / scale),
      limit: Math.round(limit / scale),
      pct: limit > 0 ? used / limit : 1,
    }
  })
  return { meters, warn: meters.some((m) => m.pct >= 0.8) }
}

const btn =
  'inline-flex cursor-pointer items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50'
const spinner = 'h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent'

function Compare() {
  const { t } = useTranslation()
  const { planGeneration, brainDump, voiceCall } = USAGE_LIMITS
  const rows: [string, string, string][] = [
    ['goals', '3', t('settings.compare.unlimited')],
    [
      'plans',
      t('settings.compare.perDay', { count: planGeneration.free.limit }),
      t('settings.compare.perDay', { count: planGeneration.pro.limit }),
    ],
    ['brainDump', '—', t('settings.compare.perMonth', { count: brainDump.pro.limit })],
    ['calls', '—', t('settings.compare.minPerMonth', { count: voiceCall.pro.limit / 60 })],
    ['accents', t('settings.compare.one'), '6'],
  ]
  return (
    <div className="px-4 py-3">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/35">
        {t('settings.compare.title')}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground">
            <th />
            <th className="pb-1 text-right font-medium">{t('settings.planFree')}</th>
            <th className="pb-1 text-right font-semibold text-primary-text">
              {t('settings.planPro')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, free, pro]) => (
            <tr key={key} className="border-t border-border/60">
              <td className="py-1.5 text-foreground/80">{t(`settings.compare.${key}`)}</td>
              <td className="py-1.5 text-right font-mono text-muted-foreground">{free}</td>
              <td className="py-1.5 text-right font-mono">{pro}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function PlanSection() {
  const { t, i18n } = useTranslation()
  const { isPro, isLoading, tier, trial, trialEligible, lifetime } = useIsPro()
  const pricing = useQuery(api.subscriptions.getPricing, isPro ? {} : 'skip')
  const { meters } = useUsageMeters()
  const startTrial = useMutation(api.subscriptions.startMyProTrial)
  const { getManagementURL } = usePolar()
  const [busy, setBusy] = useState<'trial' | 'manage' | 'checkout' | null>(null)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [lifetimeOpen, setLifetimeOpen] = useState(false)
  const paid = isPro && tier !== 'trial'

  async function handleManage() {
    setBusy('manage')
    const url = await getManagementURL()
    setBusy(null)
    if (url) window.open(url, '_blank')
    else toast.error(t('settings.manageUnavailable'))
  }

  async function handleStartTrial() {
    setBusy('trial')
    const res = await startTrial({}).catch(() => null)
    setBusy(null)
    if (res?.started) toast.success(t('trial.started'))
    else toast.error(t('trial.startError'))
  }

  // Always through the paywall, so every plan (monthly, annual, lifetime) is offered.
  function handleUpgrade() {
    setUpgradeOpen(true)
  }

  const trialEnd = trial.endsAt
    ? new Date(trial.endsAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
    : ''

  return (
    <SettingsSection id="plan" title={t('settings.sec.plan')}>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="pro" />
      <UpgradeDialog
        open={lifetimeOpen}
        onOpenChange={setLifetimeOpen}
        reason="lifetime"
        onlyPlans={['lifetime']}
      />
      <SettingsGroup>
        {isLoading ? (
          <div className="flex items-center gap-4 px-4 py-3.5">
            <span className="hidden size-8 shrink-0 animate-pulse rounded-lg bg-muted sm:block" />
            <div className="min-w-0 flex-1">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="mt-1.5 h-3 w-48 max-w-full animate-pulse rounded bg-muted/60" />
            </div>
            <div className="h-7 w-20 shrink-0 animate-pulse rounded-full bg-muted" />
          </div>
        ) : (
          <SettingsRow
            id={rowId('settings.plan')}
            icon={<SparklesIcon className="h-4 w-4" />}
            label={
              trial.active
                ? t('trial.badge', { count: trial.daysLeft })
                : lifetime
                  ? t('settings.planLifetime')
                  : isPro
                    ? t('settings.planPro')
                    : t('settings.planFree')
            }
            description={
              trial.active
                ? t('settings.trialEnds', { date: trialEnd })
                : lifetime
                  ? t('settings.planLifetimeDesc')
                  : isPro
                    ? t('settings.planProDesc')
                    : t('settings.planFreeDesc')
            }
          >
            <div className="flex flex-wrap items-center justify-end gap-2">
              {paid ? (
                <button type="button" onClick={handleManage} disabled={!!busy} className={btn}>
                  {busy === 'manage' ? (
                    <span className={spinner} />
                  ) : (
                    <ExternalLinkIcon className="h-3 w-3" />
                  )}
                  {t('settings.manage')}
                </button>
              ) : (
                <>
                  {trialEligible && (
                    <button
                      type="button"
                      onClick={handleStartTrial}
                      disabled={!!busy}
                      className={btn}
                    >
                      {t('trial.startShort')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleUpgrade}
                    disabled={!!busy}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-white transition-all hover:bg-primary/90 disabled:opacity-50 dark:text-[#0a0a0a]"
                  >
                    <SparklesIcon className="h-3 w-3" />
                    {t('settings.upgrade')}
                  </button>
                </>
              )}
            </div>
          </SettingsRow>
        )}
        {paid && !lifetime && (pricing?.available.lifetime ?? false) && (
          <SettingsRow
            id={rowId('settings.lifetimeRow')}
            icon={<InfinityIcon className="h-4 w-4" />}
            label={t('settings.lifetimeRow')}
            description={t('settings.lifetimeRowDesc', {
              left: pricing?.lifetimeSeatsLeft ?? 0,
            })}
          >
            <button
              type="button"
              onClick={() => setLifetimeOpen(true)}
              className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 transition-all hover:bg-amber-300"
            >
              {t('settings.lifetimeCta', { price: `$${pricing?.prices.lifetime ?? 149}` })}
            </button>
          </SettingsRow>
        )}
        {meters ? (
          <div
            id={rowId('settings.usage')}
            className="grid scroll-mt-24 grid-cols-[repeat(auto-fit,minmax(min(100%,160px),1fr))] gap-x-4 gap-y-3 px-4 py-3.5"
          >
            {meters.map((m) => (
              <div key={m.feature}>
                <p className="flex justify-between gap-2 text-[11px] text-muted-foreground">
                  <span className="truncate">
                    {t('settings.meterWithPeriod', {
                      label: t(`settings.meter.${m.feature}`),
                      period: t(`settings.period.${m.period}`),
                    })}
                  </span>
                  <b className="shrink-0 font-mono font-medium text-foreground">
                    {m.used}/{m.limit}
                  </b>
                </p>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                  <i
                    className={cn(
                      'block h-full rounded-full',
                      m.pct >= 1 ? 'bg-destructive' : m.pct >= 0.8 ? 'bg-amber-400' : 'bg-primary',
                    )}
                    style={{ width: `${Math.min(100, m.pct * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          !isLoading && !isPro && <Compare />
        )}
      </SettingsGroup>
    </SettingsSection>
  )
}
